import { useContext, useEffect, useRef, useState } from "react";

// hooks
import { useValidateFetchedAccountFields } from "../../../../../hooks/useFetchedFields";

// functions
import { AppdataContext, AppdataDispatchContext } from "../../../../../functions/appdataContext";
import { initiateGetBankAccounts, handleAbortBankAccounts as handleAbort } from "../../../../../functions/billectaCalls";
import { deviceType } from "../../../../../functions/ua";
import { findGlobalContent } from "../../../../../functions/findGlobalContent";

// components
import InputField from "../../../../InputField/InputField";
import Button from "../../../../Button/Button";
import Modal from "../../../../Modal/Modal";
import Card from "../../../../Card/Card";
import Typography from "../../../../Typography/Typography";
import Icon from "../../../../Icon/Icon";
import Select from "../../../../Select/Select";

// styles
import styles from "./BankAccountForm.module.scss";

const BankaccountForm = ({
  globalData,
  formData,
  onChangeInput,
  titlePrefixNumber,
  keys,
  texts,
  labels,
  placeholderOptions,
  manualBankAccountFormIsOpen,
  setManualBankAccountFormIsOpen,
  handleInputFocus = () => {},
  handleValidation = () => {},
}) => {
  const [bankAccountWasFetched, setBankAccountWasFetched] = useState(false);

  const appdata = useContext(AppdataContext);
  const dispatch = useContext(AppdataDispatchContext);
  const stateRef = useRef();
  const clearingNumberInputRef = useRef(null);

  stateRef.current = appdata;

  const banks = appdata?.initialServerData?.billecta?.banks;
  const billectaRequestState = appdata?.billectaAutogiro?.requestState;

  const isMobile = deviceType === "mobile";

  const shouldShowFailedModal = billectaRequestState === "failed-bankaccounts";
  const shouldShowIframeModal = billectaRequestState === "requesting-bankaccounts";

  const canFetchBankAccount = formData?.socialsecuritynumber?.length === 12;

  const performInitiateGetBankAccounts = () => {
    initiateGetBankAccounts(
      {
        socialsecuritynumber: formData?.socialsecuritynumber,
        otherDevice: !isMobile,
      },
      stateRef,
      dispatch
    );
  };

  useValidateFetchedAccountFields(bankAccountWasFetched);

  const handleCloseModal = () => {
    handleAbort(dispatch);

    setTimeout(() => {
      if (clearingNumberInputRef.current) {
        clearingNumberInputRef.current.focus();
        clearingNumberInputRef.current.select();
        clearingNumberInputRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, 0);
  };

  useEffect(() => {
    if (billectaRequestState === "success-bankaccounts") {
      setManualBankAccountFormIsOpen(true);
      setBankAccountWasFetched(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billectaRequestState]);

  return (
    <>
      <Card
        title={`${titlePrefixNumber}. ${findGlobalContent({
          globalData,
          key: keys.title,
          defaultValue: texts.title,
        })}`}
        text={findGlobalContent({ globalData, key: keys.text })}
      >
        <div className={styles["content-wrapper"]}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <Button
              onClick={performInitiateGetBankAccounts}
              disabled={!canFetchBankAccount}
              isBankIdButton
              id="get-bank-account-button"
              billectaRequestState={billectaRequestState}
            >
              {billectaRequestState === "requesting-bankaccounts"
                ? "Anropar BankID..."
                : findGlobalContent({
                    globalData,
                    key: keys.getBankAccount,
                    defaultValue: texts.getBankAccount,
                  })}
            </Button>

            {Boolean(
              !canFetchBankAccount && !manualBankAccountFormIsOpen
            ) && (
              <Typography variant="button-hint-text">
                {findGlobalContent({
                  globalData,
                  key: keys.getBankAccountHint,
                  defaultValue: texts.getBankAccountHint,
                })}
              </Typography>
            )}
          </div>

          <Modal
            isActive={shouldShowIframeModal}
            closeModal={handleCloseModal}
            variant="white"
            size="medium"
            noPadding
            showCloseButton={false}
          >
            <div
              id="billecta-bank-account-selector" 
              className={styles["billecta-bank-account-selector"]}
            >
              <div className={styles["loader-container"]}>
                <Icon 
                  variant="loader" 
                  size="large"
                />
              </div>
            </div>
          </Modal>

          {/* Error Modal */}
          <Modal
            isActive={shouldShowFailedModal}
            closeModal={() => handleAbort(dispatch)}
            variant="white"
            size="small"
          >
            {billectaRequestState === "failed-bankaccounts" && (
              <>
                <div className={styles["failed-message-wrapper"]}>
                  <Icon
                    variant="info-triangle"
                    className={styles["warning-icon"]}
                    size="large"
                  />

                  <Typography variant="headline-2">
                    {texts.signErrorTitle}
                  </Typography>

                  <Typography>
                    {texts.signErrorMessageBankAccount}
                  </Typography>
                </div>

                <div className={styles["failed-message-button-wrapper"]}>
                  <Button
                    onClick={performInitiateGetBankAccounts}
                    disabled={!canFetchBankAccount}
                    isBankIdButton
                    id="retry-get-bank-account-button"
                    className={styles["retry-button"]}
                  >
                    {texts.signErrorRetryButton}
                  </Button>

                  <Button
                    onClick={() => {
                      handleCloseModal();
                      setManualBankAccountFormIsOpen(true);
                    }}
                    id="close-modal"
                    variant="tertiary"
                  >
                   {texts.fillBankAccountManually}
                  </Button>
                </div>
              </>
            )}
          </Modal>

          {!manualBankAccountFormIsOpen && (
            <Button
              variant="tertiary"
              onClick={() => setManualBankAccountFormIsOpen(true)}
              id="open-manual-bank-account-form-button"
            >
              {globalData?.button_manualBankAccount
                ? globalData.button_manualBankAccount
                : "Fyll i kontouppgifterna själv"}
            </Button>
          )}

          {manualBankAccountFormIsOpen && (
              <div className={styles["account-input-wrapper"]}>
                {banks && banks.length > 0 && (
                  <Select
                    id="dropdown-select-bank"
                    name="bank"
                    value={formData?.bank || ""}
                    onChange={onChangeInput}
                    label={findGlobalContent({
                      globalData,
                      key: keys.bankSelect,
                      innerKey: "label",
                      defaultValue: labels.bankSelect || "Välj bank",
                    })}
                  >
                    <option value="">
                      {findGlobalContent({
                        globalData,
                        key: keys.bankSelect,
                        innerKey: "placeholderOption",
                        defaultValue: placeholderOptions?.bankSelect || "Välj bank",
                      })}
                    </option>

                    {banks.map((element, index) => (
                      <option key={index} value={element.id}>
                        {element.name}
                      </option>
                    ))}
                  </Select>
                )}

                <InputField
                  type="tel"
                  name="clearing"
                  id="clearing-number-input"
                  ref={clearingNumberInputRef}
                  value={formData.clearing}
                  onChange={onChangeInput}
                  onFocus={(event) => handleInputFocus(event)}
                  onBlur={(event) => handleValidation(event)}
                  globalKey={keys.bankClearing}
                  defaultValues={{
                    label: labels.bankClearing,
                  }}
                />

                <InputField
                  type="tel"
                  name="account"
                  id="account-number-input"
                  value={formData?.account}
                  onChange={onChangeInput}
                  onFocus={(event) => handleInputFocus(event)}
                  onBlur={(event) => handleValidation(event)}
                  globalKey={keys.bankAccount}
                  defaultValues={{
                    label: labels.bankAccount,
                  }}
                />
              </div>
            )}
        </div>
      </Card>
    </>
  );
};

export default BankaccountForm;
